import { useState, useEffect } from 'react';
import { getBhashyams, createBhashyam, updateBhashyam, deleteBhashyam } from '../services/bhashyams';
import { Bhashyam, BhashyamCreate, BhashyamUpdate, PHILOSOPHIES, LANGUAGES, LanguageKey, PhilosophyKey } from '../types/bhashyam';
import { useConfirm } from '../contexts/ConfirmDialogContext';

interface BhashyamsListProps {
  projectName: string;
  section: string;
  chapter: number;
  number: number;
}

export const BhashyamsList = ({ projectName, section, chapter, number }: BhashyamsListProps) => {
  const [bhashyams, setBhashyams] = useState<Bhashyam[]>([]);
  console.log('Component mounted with params:', { projectName, section, chapter, number });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newBhashyam, setNewBhashyam] = useState<BhashyamCreate>({
    text: '',
    language: 'sa', // Default to Sanskrit
    philosophy: 'adv'
  });
  const [editingBhashyam, setEditingBhashyam] = useState<Bhashyam | null>(null);

  const confirm = useConfirm();
  

  useEffect(() => {
    console.log('useEffect triggered, calling fetchBhashyams');
    fetchBhashyams();
  }, [projectName, section, chapter, number]);

  const fetchBhashyams = async () => {
    console.log('Missing required params:', { projectName, section, chapter, number });
    try {
      console.log('Starting to fetch bhashyams');
      setLoading(true);
      setError(null);
      const data = await getBhashyams(projectName, section, chapter, number);
      console.log('Fetched transliterations:', data);
      setBhashyams(data);
    } catch (err) {
      if (err instanceof Error && err.message.includes('404')) {
        // If no bhashyams exist yet, that's fine - we'll show the add form
        setBhashyams([]);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to fetch bhashyams');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setError(null);
      console.log('Creating bhashyam with:', { projectName, section, chapter, number, newBhashyam });
      const created = await createBhashyam(projectName, section, chapter, number, newBhashyam);
      console.log('Created bhashyam:', created);
      setNewBhashyam({ text: '', language: 'sa', philosophy: 'adv' });
      fetchBhashyams();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create bhashyam');
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingBhashyam) return;
    try {
      console.log('Updating bhashyam with:', { projectName, section, chapter, number, editingBhashyam });
      setError(null);
      const updated = await updateBhashyam(projectName, section, chapter, number, editingBhashyam);
      console.log('Updated bhashyam:', updated);
      setEditingBhashyam(null);
      fetchBhashyams();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update bhashyam');
    }
  };

  const handleDelete = async (language: string, philosophy: string) => {
    const ok = await confirm({
        title: 'Confirm Delete',
        message: `Are you sure you want to delete ...?`,
        confirmText: 'Delete',
        cancelText: 'Cancel',
    });

    if (!ok) return;

    try {
      console.log('Deleting bhashyam with:', { projectName, section, chapter, number, language, philosophy });
      setError(null);
      await deleteBhashyam(projectName, section, chapter, number, language, philosophy);
      fetchBhashyams();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete bhashyam');
    }
  };

  if (loading) {
    return <div className="p-4">Loading bhashyams...</div>;
  }

  return (
    <div className="p-4">
      {error && <div className="text-red-500 mb-4">{error}</div>}
      
      <h2 className="text-2xl font-bold mb-4">Bhashyam for Sutra {section}/{chapter}/{number} in {projectName}</h2>
      
      {/* Add New Bhashyam Form */}
      <form onSubmit={handleCreate} className="mb-6 p-4 border rounded">
        <h3 className="text-lg font-semibold mb-3">Add New Bhashyam</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Language</label>
            <select
              value={newBhashyam.language}
              onChange={(e) => setNewBhashyam({ ...newBhashyam, language: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            >
              {(Object.keys(LANGUAGES) as LanguageKey[]).map((key) => (
                <option key={key} value={LANGUAGES[key]}>
                  {LANGUAGES[key]}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Philosophy</label>
            <select
              value={newBhashyam.philosophy}
              onChange={(e) => setNewBhashyam({ ...newBhashyam, philosophy: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            >
              {(Object.keys(PHILOSOPHIES) as PhilosophyKey[]).map((key) => (
                <option key={key} value={PHILOSOPHIES[key]}>
                  {PHILOSOPHIES[key]}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700">Text</label>
          <textarea
            value={newBhashyam.text}
            onChange={(e) => setNewBhashyam({ ...newBhashyam, text: e.target.value })}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            rows={4}
            required
          />
        </div>
        <button
          type="submit"
          className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700"
        >
          Add Bhashyam
        </button>
      </form>

      {/* Bhashyams List */}
      <div className="space-y-4">
        {bhashyams.length === 0 ? (
          <p className="text-gray-500">No bhashyams found. Add a new bhashyam above.</p>
        ) : (
          bhashyams.map((bhashyam) => (
            <div key={`${bhashyam.language}-${bhashyam.philosophy}`} className="border rounded p-4">
              {editingBhashyam?.id === bhashyam.id ? (
                <form onSubmit={handleUpdate}>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Language</label>
                      <select
                        value={editingBhashyam.language}
                        onChange={(e) => setEditingBhashyam({ ...editingBhashyam, language: e.target.value })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      >
                        {(Object.keys(LANGUAGES) as LanguageKey[]).map((key) => (
                          <option key={key} value={LANGUAGES[key]}>
                            {LANGUAGES[key]}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Philosophy</label>
                      <select
                        value={editingBhashyam.philosophy}
                        onChange={(e) => setEditingBhashyam({ ...editingBhashyam, philosophy: e.target.value })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      >
                        {(Object.keys(PHILOSOPHIES) as PhilosophyKey[]).map((key) => (
                          <option key={key} value={PHILOSOPHIES[key]}>
                            {PHILOSOPHIES[key]}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700">Text</label>
                    <textarea
                      value={editingBhashyam.text}
                      onChange={(e) => setEditingBhashyam({ ...editingBhashyam, text: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      rows={4}
                      required
                    />
                  </div>
                  <div className="flex space-x-2">
                    <button
                      type="submit"
                      className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700"
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingBhashyam(null)}
                      className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <>
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <span className="font-semibold">{LANGUAGES[bhashyam.language as LanguageKey]}</span>
                      <span className="mx-2">•</span>
                      <span className="font-semibold">{PHILOSOPHIES[bhashyam.philosophy as PhilosophyKey]}</span>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setEditingBhashyam(bhashyam)}
                        className="text-indigo-600 hover:text-indigo-900"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(bhashyam.language, bhashyam.philosophy)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                  <p className="whitespace-pre-wrap">{bhashyam.text}</p>
                </>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}; 